/**
 * Voice model registry: the one list of text-to-speech models across every
 * voice provider (voice programme W2).
 *
 * Routes, pickers, agent tool enums, preferences and billing all read this
 * list. Zod enums are DERIVED from it (see `lib/voice/request-schemas.ts`);
 * never retype a model list elsewhere. `tests/regression/
 * voice-model-registry-contract.test.ts` checks every selectable model parses
 * through the real route schemas and has pricing, a length limit and an
 * adapter.
 *
 * Client-safe: no server imports, no runtime imports at all.
 *
 * - `enabled`: an adapter exists and the routes accept the model. A disabled
 *   entry is declared (shape proven, pricing ready) but rejected everywhere.
 * - `selectable`: offered in pickers and agent tool enums for NEW work.
 *   Enabled but unselectable models are legacy: existing takes keep playing
 *   and can be regenerated, but nothing offers them.
 */

export type VoiceProviderId = "elevenlabs" | "gemini";

/**
 * How a model takes performance markup (voice programme R10 / W5):
 * - `eleven-tags`: square-bracket audio tags in the render text.
 * - `gemini-style`: a per-turn `style` note plus angle-bracket tags.
 * - `none`: plain text only.
 */
export type VoiceExpressionStyle = "eleven-tags" | "gemini-style" | "none";

/** Billing unit. ElevenLabs bills characters; Gemini bills audio seconds. */
export type VoicePricingUnit = "per_1k_chars" | "per_second";

/** Provider settings a model honours (anything else is dropped or ignored). */
export type VoiceModelSetting =
  | "stability"
  | "similarityBoost"
  | "style"
  | "useSpeakerBoost"
  | "speed"
  | "seed"
  | "language"
  | "pronunciationDictionaries"
  | "contextText"
  | "styleNote";

export interface VoiceModelCapabilities {
  /** Provider returns per-character timing with the audio. */
  nativeAlignment: boolean;
  /** Several speakers in one request (dialogue endpoints). */
  multiSpeaker: boolean;
  /** Most speakers per request; 1 when `multiSpeaker` is false. */
  maxSpeakers: number;
  /** Audio can be streamed as it is produced. */
  streaming: boolean;
  expressionStyle: VoiceExpressionStyle;
  /** Free-text cues (not only a curated tag list) are accepted. */
  freeTextCues: boolean;
  settings: readonly VoiceModelSetting[];
}

export interface VoiceModelEntry {
  id: string;
  provider: VoiceProviderId;
  label: string;
  description: string;
  enabled: boolean;
  selectable: boolean;
  capabilities: VoiceModelCapabilities;
  /** Longest text accepted in one request, in characters. */
  maxCharacters: number;
  pricing: {
    unit: VoicePricingUnit;
    /** `MODEL_PRICING_CONFIG` key (`lib/pricing-config.ts`). */
    key: string;
  };
  /** MIME type of the audio the adapter returns. */
  outputMimeType: "audio/mpeg" | "audio/wav";
}

export interface VoiceProviderEntry {
  id: VoiceProviderId;
  label: string;
  /** Default model for new work with this provider. */
  defaultModel: string;
  /** Default voice (a persona alias or provider-native voice ID). */
  defaultVoice: string;
}

const ELEVEN_V4_SETTINGS = [
  "stability",
  "similarityBoost",
  "seed",
  "language",
  "pronunciationDictionaries",
  "contextText",
] as const satisfies readonly VoiceModelSetting[];

const ELEVEN_V2_SETTINGS = [
  "stability",
  "similarityBoost",
  "style",
  "useSpeakerBoost",
  "speed",
  "seed",
  "language",
  "pronunciationDictionaries",
  "contextText",
] as const satisfies readonly VoiceModelSetting[];

const GEMINI_SETTINGS = [
  "styleNote",
  "language",
] as const satisfies readonly VoiceModelSetting[];

/**
 * Every voice model. Order matters: pickers list selectable models in this
 * order, and the first selectable model of a provider is listed first.
 *
 * ElevenLabs limits are `maximum_text_length_per_request` from GET /v1/models
 * (checked 2026-09-29). Eleven text-to-dialogue takes up to 10 voices on v3,
 * v4 and v4 Turbo; the dialogue WebSocket takes exactly one voice per
 * connection on v4 Turbo. Every ElevenLabs model streams over HTTP `/stream`.
 */
export const VOICE_MODELS = [
  {
    id: "eleven_v4",
    provider: "elevenlabs",
    label: "Eleven v4",
    description: "Highest quality, with free-text direction and audio tags",
    enabled: true,
    selectable: true,
    capabilities: {
      nativeAlignment: true,
      multiSpeaker: true,
      maxSpeakers: 10,
      streaming: true,
      expressionStyle: "eleven-tags",
      freeTextCues: true,
      settings: ELEVEN_V4_SETTINGS,
    },
    maxCharacters: 10_000,
    pricing: { unit: "per_1k_chars", key: "eleven_v4" },
    outputMimeType: "audio/mpeg",
  },
  {
    id: "eleven_v4_turbo",
    provider: "elevenlabs",
    label: "Eleven v4 Turbo",
    description: "Fast v4 for real-time use, half the price",
    enabled: true,
    selectable: true,
    capabilities: {
      nativeAlignment: true,
      multiSpeaker: true,
      maxSpeakers: 10,
      streaming: true,
      expressionStyle: "eleven-tags",
      freeTextCues: true,
      settings: ELEVEN_V4_SETTINGS,
    },
    maxCharacters: 10_000,
    pricing: { unit: "per_1k_chars", key: "eleven_v4_turbo" },
    outputMimeType: "audio/mpeg",
  },
  {
    id: "eleven_v3",
    provider: "elevenlabs",
    label: "Eleven v3",
    description: "Legacy: expressive, with a fixed set of audio tags",
    enabled: true,
    selectable: false,
    capabilities: {
      nativeAlignment: true,
      multiSpeaker: true,
      maxSpeakers: 10,
      streaming: true,
      expressionStyle: "eleven-tags",
      freeTextCues: false,
      settings: [
        "stability",
        "seed",
        "language",
        "pronunciationDictionaries",
        "contextText",
      ],
    },
    maxCharacters: 5_000,
    pricing: { unit: "per_1k_chars", key: "eleven_v3" },
    outputMimeType: "audio/mpeg",
  },
  {
    id: "eleven_multilingual_v2",
    provider: "elevenlabs",
    label: "Multilingual v2",
    description: "Legacy: high quality, supports 29 languages",
    enabled: true,
    selectable: false,
    capabilities: {
      nativeAlignment: true,
      multiSpeaker: false,
      maxSpeakers: 1,
      streaming: true,
      expressionStyle: "none",
      freeTextCues: false,
      settings: ELEVEN_V2_SETTINGS,
    },
    maxCharacters: 10_000,
    pricing: { unit: "per_1k_chars", key: "eleven_multilingual_v2" },
    outputMimeType: "audio/mpeg",
  },
  {
    id: "eleven_turbo_v2_5",
    provider: "elevenlabs",
    label: "Turbo v2.5",
    description: "Legacy: fast, English-optimized",
    enabled: true,
    selectable: false,
    capabilities: {
      nativeAlignment: true,
      multiSpeaker: false,
      maxSpeakers: 1,
      streaming: true,
      expressionStyle: "none",
      freeTextCues: false,
      settings: ELEVEN_V2_SETTINGS,
    },
    maxCharacters: 40_000,
    pricing: { unit: "per_1k_chars", key: "eleven_turbo_v2_5" },
    outputMimeType: "audio/mpeg",
  },
  {
    id: "eleven_flash_v2_5",
    provider: "elevenlabs",
    label: "Flash v2.5",
    description: "Legacy: low latency",
    enabled: true,
    selectable: false,
    capabilities: {
      nativeAlignment: true,
      multiSpeaker: false,
      maxSpeakers: 1,
      streaming: true,
      expressionStyle: "none",
      freeTextCues: false,
      settings: ELEVEN_V2_SETTINGS,
    },
    maxCharacters: 40_000,
    pricing: { unit: "per_1k_chars", key: "eleven_flash_v2_5" },
    outputMimeType: "audio/mpeg",
  },
  // Gemini 3.8 Flash TTS (voice programme W6, `providers/gemini.ts`). Billed
  // per second of audio. Input is 8,192 tokens; 8,000 characters keeps a
  // style note inside that. No timing data (the pipeline runs a forced-
  // alignment pass); at most 2 speakers (prebuilt voices only). Output is
  // WAV: the API refuses MP3 (checked 2026-09-29).
  {
    id: "gemini-3.8-flash-tts",
    provider: "gemini",
    label: "Gemini 3.8 Flash TTS",
    description: "Google TTS with style notes, billed per second",
    enabled: true,
    selectable: true,
    capabilities: {
      nativeAlignment: false,
      multiSpeaker: true,
      maxSpeakers: 2,
      streaming: true,
      expressionStyle: "gemini-style",
      freeTextCues: true,
      settings: GEMINI_SETTINGS,
    },
    maxCharacters: 8_000,
    pricing: { unit: "per_second", key: "gemini-3.8-flash-tts" },
    outputMimeType: "audio/wav",
  },
  {
    id: "gemini-3.8-flash-lite-tts",
    provider: "gemini",
    label: "Gemini 3.8 Flash-Lite TTS",
    description: "Cheapest Google TTS, billed per second",
    enabled: true,
    selectable: true,
    capabilities: {
      nativeAlignment: false,
      multiSpeaker: true,
      maxSpeakers: 2,
      streaming: true,
      expressionStyle: "gemini-style",
      freeTextCues: true,
      settings: GEMINI_SETTINGS,
    },
    maxCharacters: 8_000,
    pricing: { unit: "per_second", key: "gemini-3.8-flash-lite-tts" },
    outputMimeType: "audio/wav",
  },
] as const satisfies readonly VoiceModelEntry[];

/** Every declared model id, enabled or not. */
export type VoiceModelId = (typeof VOICE_MODELS)[number]["id"];

export const VOICE_PROVIDERS = {
  elevenlabs: {
    id: "elevenlabs",
    label: "ElevenLabs",
    defaultModel: "eleven_v4",
    defaultVoice: "rachel",
  },
  gemini: {
    id: "gemini",
    label: "Google Gemini",
    defaultModel: "gemini-3.8-flash-tts",
    defaultVoice: "Kore",
  },
} as const satisfies Record<VoiceProviderId, VoiceProviderEntry>;

/** The default provider for new voice work (voice programme DV6). */
export const DEFAULT_VOICE_PROVIDER: VoiceProviderId = "elevenlabs";

/** The default model for every new voice generation (voice programme DV1). */
export const DEFAULT_VOICE_MODEL = "eleven_v4" satisfies VoiceModelId;

/** Default voice when none is given (a persona alias). */
export const DEFAULT_VOICE = VOICE_PROVIDERS.elevenlabs.defaultVoice;

const MODELS_BY_ID: ReadonlyMap<string, VoiceModelEntry> = new Map(
  VOICE_MODELS.map((entry) => [entry.id, entry as VoiceModelEntry]),
);

function nonEmptyTuple<T extends string>(values: T[]): [T, ...T[]] {
  if (values.length === 0) {
    throw new Error("Voice model registry produced an empty model list");
  }
  return values as [T, ...T[]];
}

/**
 * Models the routes accept (enabled: current and legacy). Suitable for
 * `z.enum(...)`.
 */
export const ENABLED_VOICE_MODEL_IDS: readonly [
  VoiceModelId,
  ...VoiceModelId[],
] = nonEmptyTuple(
  VOICE_MODELS.filter((entry) => entry.enabled).map((entry) => entry.id),
);

/** Models offered for new work (enabled and selectable). */
export const SELECTABLE_VOICE_MODEL_IDS: readonly [
  VoiceModelId,
  ...VoiceModelId[],
] = nonEmptyTuple(
  VOICE_MODELS.filter((entry) => entry.enabled && entry.selectable).map(
    (entry) => entry.id,
  ),
);

/** Look up any declared model, enabled or not. */
export function getVoiceModel(model: unknown): VoiceModelEntry | undefined {
  return typeof model === "string" ? MODELS_BY_ID.get(model) : undefined;
}

/** True for a model the routes accept (enabled, current or legacy). */
export function isVoiceModelId(model: unknown): model is VoiceModelId {
  return getVoiceModel(model)?.enabled === true;
}

/** True for a model offered for new work. */
export function isSelectableVoiceModel(model: unknown): model is VoiceModelId {
  const entry = getVoiceModel(model);
  return entry?.enabled === true && entry.selectable;
}

/** Enabled models of one provider, in registry order. */
export function getVoiceModelsForProvider(
  provider: VoiceProviderId,
  options: { selectableOnly?: boolean } = {},
): VoiceModelEntry[] {
  return VOICE_MODELS.filter(
    (entry) =>
      entry.provider === provider &&
      entry.enabled &&
      (!options.selectableOnly || entry.selectable),
  ) as VoiceModelEntry[];
}

export function isVoiceProviderId(value: unknown): value is VoiceProviderId {
  return typeof value === "string" && Object.hasOwn(VOICE_PROVIDERS, value);
}

/** The provider a model belongs to; `fallback` for unknown models. */
export function voiceProviderForModel(
  model: unknown,
  fallback: VoiceProviderId = DEFAULT_VOICE_PROVIDER,
): VoiceProviderId {
  return getVoiceModel(model)?.provider ?? fallback;
}

/**
 * Model to use for a new generation when the stored choice may be legacy
 * (for example a saved Flash v2.5 preference). Legacy, disabled and unknown
 * values become the default; selectable values are kept.
 */
export function resolveVoiceModelForNewGeneration(
  model: unknown,
): VoiceModelId {
  return isSelectableVoiceModel(model) ? model : DEFAULT_VOICE_MODEL;
}

export interface VoiceModelPickerOption {
  id: VoiceModelId;
  provider: VoiceProviderId;
  name: string;
  description: string;
  legacy: boolean;
}

/**
 * Picker options: the selectable models, plus the current value when it is a
 * legacy model so the control can still show what an existing node uses
 * ("(legacy)" suffix). `provider` limits the list to one provider (the
 * Brainstorm stream only speaks ElevenLabs).
 */
export function getVoiceModelPickerOptions(
  current?: string,
  options: { provider?: VoiceProviderId } = {},
): VoiceModelPickerOption[] {
  const entries = VOICE_MODELS.filter(
    (entry) =>
      entry.enabled &&
      (entry.selectable || entry.id === current) &&
      (!options.provider || entry.provider === options.provider),
  ) as VoiceModelEntry[];
  const ordered = [
    ...entries.filter((entry) => entry.selectable),
    ...entries.filter((entry) => !entry.selectable),
  ];
  return ordered.map((entry) => ({
    id: entry.id as VoiceModelId,
    provider: entry.provider,
    name: entry.selectable ? entry.label : `${entry.label} (legacy)`,
    description: entry.description,
    legacy: !entry.selectable,
  }));
}

/** The model's markup style; `none` for unknown models. */
export function voiceModelExpressionStyle(
  model: unknown,
): VoiceExpressionStyle {
  return getVoiceModel(model)?.capabilities.expressionStyle ?? "none";
}

/** True when the model honours the given provider setting. */
export function voiceModelSupportsSetting(
  model: unknown,
  setting: VoiceModelSetting,
): boolean {
  return getVoiceModel(model)?.capabilities.settings.includes(setting) ?? false;
}

/** Character limit for one request; undefined for unknown models. */
export function voiceModelCharacterLimit(model: unknown): number | undefined {
  return getVoiceModel(model)?.maxCharacters;
}
